import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';

const ProfileSettingsModal = ({ isOpen, onClose }) => {
  const { user, userProfile, updateProfile } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    notificationEmail: '',
    userRole: '',
    studentRole: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load user profile data when modal opens
  useEffect(() => {
    if (isOpen && userProfile) {
      setFormData({
        fullName: userProfile?.full_name || '',
        email: user?.email || '',
        notificationEmail: userProfile?.notification_email || user?.email || '',
        userRole: userProfile?.role || '',
        studentRole: userProfile?.team_role || '',
        phone: userProfile?.phone || ''
      });
      setError('');
      setSuccess('');
    }
  }, [isOpen, userProfile, user]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
    setSuccess('');
  };

  const validateForm = () => {
    if (!formData?.fullName?.trim()) {
      setError('Full Name is required');
      return false;
    }
    if (!formData?.notificationEmail?.trim()) {
      setError('Email for Notifications is required');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(formData?.notificationEmail)) {
      setError('Please enter a valid email for notifications');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Update user_profiles table
      const profileUpdates = {
        full_name: formData?.fullName?.trim(),
        notification_email: formData?.notificationEmail?.trim(),
        phone: formData?.phone?.trim() || null,
        updated_at: new Date()?.toISOString()
      };

      const { data: profileData, error: profileError } = await supabase?.from('user_profiles')?.update(profileUpdates)?.eq('id', user?.id)?.select()?.single();

      if (profileError) throw profileError;

      // Update role_email_authorizations if notification email changed
      if (formData?.notificationEmail?.trim() !== userProfile?.notification_email) {
        const { error: roleAuthError } = await supabase?.from('role_email_authorizations')?.update({ 
            email: formData?.notificationEmail?.trim(),
            updated_at: new Date()?.toISOString()
          })?.eq('email', user?.email);

        if (roleAuthError) {
          console.warn('Could not update role_email_authorizations:', roleAuthError);
        }
      }

      // Update local context
      await updateProfile(profileUpdates);

      setSuccess('Profile updated successfully!');
      setTimeout(() => {
        onClose?.();
      }, 1500);

    } catch (err) {
      console.error('Profile update error:', err);
      setError(err?.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getRoleDisplayName = (role) => {
    const roleMap = {
      'city_official': 'City Official',
      'student': 'Student',
      'pcf_admin': 'PCF Admin',
      'team_member': 'Team Member'
    };
    return roleMap?.[role] || role;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
      <div className="bg-background rounded-lg w-full max-w-md mx-3 sm:mx-4 border border-border max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-semibold text-foreground">Profile Settings</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              disabled={loading}
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Success Message */}
          {success && (
            <div className="p-3 bg-green-500/10 border border-green-500/20 rounded text-green-600 text-sm flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} className="flex-shrink-0" />
              <span>{success}</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive text-sm flex items-start space-x-2">
              <Icon name="AlertCircle" size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Full Name (Editable) */}
          <div>
            <Input
              label="Full Name"
              type="text"
              placeholder="Enter your full name"
              value={formData?.fullName}
              onChange={(e) => handleChange('fullName', e?.target?.value)}
              required
              disabled={loading}
            />
          </div>

          {/* Email Address (Read-only) */}
          <div>
            <Input
              label="Email Address (Login Email)"
              type="email"
              value={formData?.email}
              disabled
              className="bg-muted cursor-not-allowed"
            />
            <p className="text-xs text-muted-foreground mt-1">This is your login email and cannot be changed</p>
          </div>

          {/* Email for Notifications (Editable) */}
          <div>
            <Input
              label="Email for Notifications"
              type="email"
              placeholder="Enter notification email"
              value={formData?.notificationEmail}
              onChange={(e) => handleChange('notificationEmail', e?.target?.value)}
              required
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground mt-1">Receive all notifications at this email</p>
          </div>

          {/* User Role (Read-only) */}
          <div>
            <Input
              label="User Role"
              type="text"
              value={getRoleDisplayName(formData?.userRole)}
              disabled
              className="bg-muted cursor-not-allowed"
            />
          </div>

          {/* Student Role (Read-only, only shown if role is student) */}
          {formData?.userRole === 'student' && formData?.studentRole && (
            <div>
              <Input
                label="Student Role"
                type="text"
                value={getRoleDisplayName(formData?.studentRole)}
                disabled
                className="bg-muted cursor-not-allowed"
              />
            </div>
          )}

          {/* Phone Number (Editable) */}
          <div>
            <Input
              label="Phone Number"
              type="tel"
              placeholder="Enter your phone number"
              value={formData?.phone}
              onChange={(e) => handleChange('phone', e?.target?.value)}
              disabled={loading}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={loading}
              className="min-w-[100px]"
            >
              {loading ? (
                <>
                  <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileSettingsModal;